import React from 'react';
import { classNames } from '../../helper/classNames';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  /** Shows a spinner, keeps the label in the a11y tree and blocks clicks. */
  isLoading?: boolean;
  /** Trailing arrow, e.g. for «Weiter»-Aktionen. */
  showArrow?: boolean;
}

// Alle Varianten erfuellen WCAG 2.2 AA (>= 4.5:1 fuer Text).
// Achtung: primary-500 ist im Token-Set als «LOGO - Primary button color»
// kommentiert, erreicht mit weisser Schrift aber nur 3.5:1 und ist daher
// hier bewusst NICHT die Button-Flaeche. Siehe packages/ui/README.md.
const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary-600 text-white hover:bg-primary-700',
  secondary: 'bg-secondary-700 text-white hover:bg-secondary-800',
  outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
  ghost: 'text-primary-600 hover:bg-primary-50 hover:text-primary-700',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'gap-1.5 px-3 py-1.5 text-xs',
  md: 'gap-2 px-4 py-2.5 text-sm',
  lg: 'gap-2 px-6 py-3 text-base',
};

// Kein `dark:`-Styling: nur immodossier-client faehrt `darkMode: 'class'`,
// die uebrigen Apps laufen auf Tailwinds `media`-Default. Dark-Varianten
// hier wuerden dort bei OS-Dunkelmodus unbeabsichtigt greifen. Apps mit
// Dark Mode ergaenzen ihre Klassen per `className`.
const BASE =
  'inline-flex items-center justify-center rounded-lg font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  isLoading = false,
  showArrow = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      aria-busy={isLoading || undefined}
      disabled={isLoading || disabled}
      className={classNames(
        BASE,
        VARIANTS[variant],
        SIZES[size],
        fullWidth ? 'w-full' : undefined,
        className,
      )}
    >
      {isLoading ? (
        <span className="relative inline-flex items-center justify-center">
          {/* Label bleibt im A11y-Baum, nur visuell ersetzt. */}
          <span className="opacity-0">{children}</span>
          <span
            aria-hidden="true"
            className="absolute inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        </span>
      ) : (
        <>
          {children}
          {showArrow ? (
            <svg
              aria-hidden="true"
              className="h-4 w-4 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          ) : null}
        </>
      )}
    </button>
  );
}
